import React, { useState, useRef, useEffect } from 'react';
import { Mail, Bell, Zap, Image, Paperclip, Variable, Upload, Info, Loader2, CheckCircle, AlertCircle, Save } from 'lucide-react';
import useSuperAdminMarketingStore from '@/stores/SuperAdminStore/SuperAdminMarketingStore';
import { Audience, CampaignData, NotificationRecord, validateEmails, uploadMarketingImage, uploadMarketingAttachment } from '@/services/PagesServices/SuperAdminMarketingService';
import './ComposeNotification.css';

interface ComposeNotificationProps {
  // When set, the form opens in edit mode pre-filled with this draft/scheduled
  // campaign; saving updates it instead of creating a new one.
  editSeed?: NotificationRecord | null;
  onEditConsumed?: () => void;
}

const ComposeNotification: React.FC<ComposeNotificationProps> = ({ editSeed, onEditConsumed }) => {
  const { audiences: storeAudiences, audienceUsers, sending, createAndSendCampaign, updateAndSendCampaign, fetchAudienceUsers } = useSuperAdminMarketingStore();

  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'email' | 'in-app' | 'both'>('email');
  const [audiences, setAudiences] = useState<Audience[]>(storeAudiences || []);
  // Specific individuals selected within each group (by user uuid).
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  // Which group's individual-picker dropdown is currently open.
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvEmails, setCsvEmails] = useState<string[]>([]);
  const [csvInvalidEmails, setCsvInvalidEmails] = useState<string[]>([]);
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Personalization tokens that get substituted per-recipient when sending.
  const VARIABLES = ['{{firstName}}', '{{lastName}}', '{{email}}', '{{courseName}}'];

  // Insert text at the textarea cursor (or append if not focused).
  const insertIntoMessage = (text: string) => {
    const el = messageRef.current;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = message.slice(0, start) + text + message.slice(end);
      setMessage(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + text.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setMessage(prev => (prev ? `${prev} ${text}` : text));
    }
  };

  const insertVariable = (token: string) => {
    insertIntoMessage(token);
    setShowVariableMenu(false);
  };

  // The email template renders the message as raw HTML, so we embed an <img>
  // for images and an <a> link for attachments at the cursor position.
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploadingMedia(true);
    try {
      const res = await uploadMarketingImage(file);
      if (res.success && res.url) {
        insertIntoMessage(`\n<img src="${res.url}" alt="" style="max-width:100%;height:auto;" />\n`);
        setFeedback({ type: 'success', message: 'Image added to the message.' });
      } else {
        setFeedback({ type: 'error', message: res.error?.message || 'Failed to upload image' });
      }
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploadingMedia(true);
    try {
      const res = await uploadMarketingAttachment(file);
      if (res.success && res.url) {
        const label = res.originalName || file.name;
        insertIntoMessage(`\n<a href="${res.url}">${label}</a>\n`);
        setFeedback({ type: 'success', message: 'Attachment link added to the message.' });
      } else {
        setFeedback({ type: 'error', message: res.error?.message || 'Failed to upload attachment' });
      }
    } finally {
      setUploadingMedia(false);
    }
  };

  // Roles to keep checked while editing (applied once the audience groups load).
  const [editRoleIds, setEditRoleIds] = useState<number[] | null>(null);

  // Sync store data to local state. When editing, re-apply the draft's role
  // selections so they survive the audience groups loading after prefill.
  useEffect(() => {
    if (storeAudiences) {
      setAudiences(storeAudiences.map((a) => ({
        ...a,
        checked: editRoleIds ? (a.role !== undefined && editRoleIds.includes(a.role)) : false,
      })));
    }
  }, [storeAudiences, editRoleIds]);

  // Convert a UTC/ISO timestamp to a datetime-local value in IST (YYYY-MM-DDTHH:mm).
  const isoToISTInputValue = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(d);
    const get = (t: string) => (parts.find((p) => p.type === t) || ({} as any)).value || '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
  };

  // Prefill the form when opened in edit mode.
  useEffect(() => {
    if (!editSeed) return;
    setEditingUuid(editSeed.uuid || null);
    setNotificationTitle(editSeed.title || '');
    setMessage(editSeed.message || '');
    setNotificationType((editSeed.type as 'email' | 'in-app' | 'both') || 'email');
    setEditRoleIds(editSeed.roles || []);
    setSelectedUserIds(new Set(editSeed.specificUserIds || []));
    setCsvEmails(editSeed.specificEmails || []);
    setCsvInvalidEmails([]);
    setUploadedFile(null);
    if (editSeed.scheduledForRaw) {
      setScheduleType('schedule');
      setScheduleDate(isoToISTInputValue(editSeed.scheduledForRaw));
    } else {
      setScheduleType('now');
      setScheduleDate('');
    }
    fetchAudienceUsers();
    onEditConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSeed]);

  // Auto-hide feedback after 5 seconds
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Load the individual users (per role) for the audience pickers.
  useEffect(() => {
    fetchAudienceUsers();
  }, [fetchAudienceUsers]);

  // Toggle "All <group>". Selecting the whole group clears any individual
  // picks for that group (they're already covered).
  const handleAudienceToggle = (group: Audience) => {
    const nextChecked = !group.checked;
    setAudiences(audiences.map(aud =>
      aud.id === group.id ? { ...aud, checked: nextChecked } : aud
    ));
    if (nextChecked && group.role !== undefined) {
      setSelectedUserIds(prev => {
        const next = new Set(prev);
        (audienceUsers || []).forEach(u => { if (u.roleId === group.role) next.delete(u.uuid); });
        return next;
      });
    }
  };

  const usersInGroup = (group: Audience) =>
    (audienceUsers || []).filter(u => u.roleId === group.role);

  const toggleUser = (uuid: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
      return next;
    });
  };

  // Count of individuals picked within a group (0 when the whole group is selected).
  const groupSelectedCount = (group: Audience) =>
    usersInGroup(group).filter(u => selectedUserIds.has(u.uuid)).length;

  // Parse a CSV file, extract emails, and validate them against existing users.
  const processCsvFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    if (!file.name.endsWith('.csv')) {
      alert('Only CSV files are supported');
      return;
    }
    setUploadedFile(file);

    try {
      const text = await file.text();
      const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
      const extracted = text.match(emailRegex) || [];
      const uniqueEmails = Array.from(new Set(extracted.map(e => e.toLowerCase())));

      if (uniqueEmails.length === 0) {
        setCsvEmails([]);
        setCsvInvalidEmails([]);
        setFeedback({ type: 'error', message: 'No valid email addresses found in the CSV.' });
        return;
      }

      const result = await validateEmails(uniqueEmails);
      if (result.success && result.data) {
        const { validEmails, invalidEmails } = result.data;
        setCsvEmails(validEmails);
        setCsvInvalidEmails(invalidEmails);
        setFeedback({
          type: validEmails.length > 0 ? 'success' : 'error',
          message: `${validEmails.length} valid recipient(s) found` +
            (invalidEmails.length > 0 ? `, ${invalidEmails.length} not in the system (ignored).` : '.')
        });
      } else {
        setCsvEmails([]);
        setCsvInvalidEmails([]);
        setFeedback({ type: 'error', message: result.error?.message || 'Failed to validate emails from CSV.' });
      }
    } catch (err) {
      setCsvEmails([]);
      setCsvInvalidEmails([]);
      setFeedback({ type: 'error', message: 'Could not read the CSV file.' });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processCsvFile(file);
  };

  // Generate and download a sample recipients CSV (an `email` column with examples).
  const handleDownloadTemplate = () => {
    const csv = 'email\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recipients-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) processCsvFile(file);
  };

  // Total targets across all selectors: role segments + picked users + CSV recipients.
  const selectedCount = audiences.filter(a => a.checked).length + selectedUserIds.size + csvEmails.length;

  // Current local date-time as YYYY-MM-DDTHH:mm. Computed fresh on each call so
  // validation always compares against the real "now" (not a stale render value).
  const nowLocalDateTime = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const minDateTime = nowLocalDateTime();

  // Reject a past date/time at selection time. The native datetime-local `min`
  // only reliably constrains the date, not the time on the current day.
  const handleScheduleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && value < nowLocalDateTime()) {
      setFeedback({ type: 'error', message: 'Schedule date and time cannot be in the past' });
      setScheduleDate('');
      return;
    }
    setScheduleDate(value);
  };

  const resetForm = () => {
    setEditingUuid(null);
    setEditRoleIds(null);
    setNotificationTitle('');
    setMessage('');
    setNotificationType('email');
    setAudiences(storeAudiences?.map(a => ({ ...a, checked: false })) || []);
    setSelectedUserIds(new Set());
    setExpandedGroup(null);
    setUploadedFile(null);
    setCsvEmails([]);
    setCsvInvalidEmails([]);
    setScheduleType('now');
    setScheduleDate('');
  };

  const handleSaveAsDraft = async () => {
    // Validation
    if (!notificationTitle.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a notification title' });
      return;
    }

    if (!message.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a message' });
      return;
    }

    const selectedAudiences = audiences.filter(a => a.checked);
    if (selectedAudiences.length === 0 && selectedUserIds.size === 0 && csvEmails.length === 0) {
      setFeedback({ type: 'error', message: 'Please select at least one audience, pick specific recipients, or upload a CSV' });
      return;
    }

    setIsSavingDraft(true);

    try {
      // Build campaign data for draft
      const campaignData: CampaignData = {
        title: notificationTitle.trim(),
        subject: notificationTitle.trim(),
        message: message.trim(),
        targetAudience: {
          roles: selectedAudiences.map(a => a.role).filter((r): r is number => r !== undefined),
          ...(selectedUserIds.size > 0 ? { specificUsers: Array.from(selectedUserIds) } : {}),
          ...(csvEmails.length > 0 ? { specificEmails: csvEmails } : {}),
        },
        deliveryMethod: notificationType,
        // Keep the planned schedule date on the draft (so it shows in history),
        // but mark it 'draft' so it stays a draft and is not auto-sent.
        scheduledFor: scheduleType === 'schedule' && scheduleDate ? scheduleDate.replace('T', ' ') : null,
        status: 'draft',
      };

      // Save as draft (sendImmediately = false, no schedule). Update when editing.
      const result = editingUuid
        ? await updateAndSendCampaign(editingUuid, campaignData, false)
        : await createAndSendCampaign(campaignData, false);

      if (result.success) {
        setFeedback({ type: 'success', message: editingUuid ? 'Draft updated successfully!' : 'Notification saved as draft successfully!' });
        resetForm();
      } else {
        setFeedback({ type: 'error', message: result.error || 'Failed to save draft' });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: 'An error occurred while saving draft' });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSendNotification = async () => {
    // Validation
    if (!notificationTitle.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a notification title' });
      return;
    }

    if (!message.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a message' });
      return;
    }

    const selectedAudiences = audiences.filter(a => a.checked);
    if (selectedAudiences.length === 0 && selectedUserIds.size === 0 && csvEmails.length === 0) {
      setFeedback({ type: 'error', message: 'Please select at least one audience, pick specific recipients, or upload a CSV' });
      return;
    }

    if (scheduleType === 'schedule' && !scheduleDate) {
      setFeedback({ type: 'error', message: 'Please select a schedule date' });
      return;
    }

    if (scheduleType === 'schedule' && scheduleDate < nowLocalDateTime()) {
      setFeedback({ type: 'error', message: 'Schedule date and time cannot be in the past' });
      return;
    }

    // Build campaign data
    const campaignData: CampaignData = {
      title: notificationTitle.trim(),
      subject: notificationTitle.trim(),
      message: message.trim(),
      targetAudience: {
        roles: selectedAudiences.map(a => a.role).filter((r): r is number => r !== undefined),
        ...(selectedUserIds.size > 0 ? { specificUsers: Array.from(selectedUserIds) } : {}),
        ...(csvEmails.length > 0 ? { specificEmails: csvEmails } : {}),
      },
      deliveryMethod: notificationType,
      scheduledFor: scheduleType === 'schedule' ? scheduleDate.replace('T', ' ') : null,
      ...(scheduleType === 'schedule' ? { status: 'scheduled' as const } : {}),
    };

    const sendImmediately = scheduleType === 'now';
    const result = editingUuid
      ? await updateAndSendCampaign(editingUuid, campaignData, sendImmediately)
      : await createAndSendCampaign(campaignData, sendImmediately);

    if (result.success) {
      setFeedback({
        type: 'success',
        message: sendImmediately
          ? 'Notification sent successfully!'
          : 'Notification scheduled successfully!'
      });
      resetForm();
    } else {
      setFeedback({
        type: 'error',
        message: result.error || 'Failed to send notification'
      });
    }
  };

  return (
    <div className="s-admin-marketing-compose-content">
      <h2 className="s-admin-marketing-section-title">{editingUuid ? 'Edit Notification' : 'Compose Notification'}</h2>
      <p className="s-admin-marketing-section-subtitle">
        {editingUuid ? 'Editing a saved draft — update and save or send it.' : 'Create and send notifications to your users'}
      </p>

      <div className="s-admin-marketing-compose-layout">
        <div className="left-panel">
          <div className="s-admin-marketing-compose-card">
            <h3 className="s-admin-marketing-card-title">Message Details</h3>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Notification Title</label>
              <input
                type="text"
                className="s-admin-marketing-form-input"
                placeholder="e.g., New Course Available"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
              />
            </div>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Message</label>
              <textarea
                ref={messageRef}
                className="s-admin-marketing-form-textarea"
                placeholder="Write your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            <div className="s-admin-marketing-toolbar" style={{ position: 'relative' }}>
              <button
                type="button"
                className="s-admin-marketing-toolbar-btn"
                title="Add Image"
                disabled={uploadingMedia}
                onClick={() => imageInputRef.current?.click()}
              >
                <Image size={16} />
                <span>Image</span>
              </button>
              <button
                type="button"
                className="s-admin-marketing-toolbar-btn"
                title="Attach File"
                disabled={uploadingMedia}
                onClick={() => attachInputRef.current?.click()}
              >
                <Paperclip size={16} />
                <span>Attach</span>
              </button>
              <button
                type="button"
                className="s-admin-marketing-toolbar-btn"
                title="Insert a personalization variable"
                onClick={() => setShowVariableMenu(v => !v)}
              >
                <Variable size={16} />
                <span>Variable</span>
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <input
                ref={attachInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip"
                onChange={handleAttachmentUpload}
                style={{ display: 'none' }}
              />
              {showVariableMenu && (
                <div className="s-admin-marketing-variable-menu" style={{
                  position: 'absolute', top: '100%', left: 0, zIndex: 10,
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: 4, minWidth: 160
                }}>
                  {VARIABLES.map(token => (
                    <button
                      key={token}
                      type="button"
                      onClick={() => insertVariable(token)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '8px 12px', border: 'none', background: 'transparent',
                        cursor: 'pointer', borderRadius: 6, fontSize: 14
                      }}
                    >
                      {token}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="s-admin-marketing-compose-card">
            <h3 className="s-admin-marketing-card-title">Delivery Settings</h3>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Notification Type</label>
              <div className="s-admin-marketing-notification-types">
                <button
                  className={`s-admin-marketing-type-btn ${notificationType === 'email' ? 's-admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('email')}
                >
                  <Mail size={20} />
                  <span>Email</span>
                </button>
                <button
                  className={`s-admin-marketing-type-btn ${notificationType === 'in-app' ? 's-admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('in-app')}
                >
                  <Bell size={20} />
                  <span>In-App</span>
                </button>
                <button
                  className={`s-admin-marketing-type-btn ${notificationType === 'both' ? 's-admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('both')}
                >
                  <Zap size={20} />
                  <span>Both</span>
                </button>
              </div>
            </div>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Select Audience</label>
              <div className="s-admin-marketing-audience-list">
                {audiences.map(audience => {
                  const groupUsers = usersInGroup(audience);
                  const picked = groupSelectedCount(audience);
                  const isOpen = expandedGroup === audience.id;
                  return (
                    <div key={audience.id} className="s-admin-marketing-audience-item" style={{ display: 'block' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={audience.checked}
                            onChange={() => handleAudienceToggle(audience)}
                            className="s-admin-marketing-audience-checkbox"
                          />
                          <span className="s-admin-marketing-audience-name">{audience.name}</span>
                          <span className="s-admin-marketing-audience-count">({audience.userCount} users)</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setExpandedGroup(isOpen ? null : audience.id)}
                          disabled={audience.checked || groupUsers.length === 0}
                          style={{
                            background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
                            padding: '4px 10px', cursor: audience.checked ? 'not-allowed' : 'pointer', fontSize: 13
                          }}
                        >
                          {picked > 0 ? `${picked} selected` : 'Choose specific'} ▾
                        </button>
                      </div>
                      {isOpen && !audience.checked && (
                        <div style={{ marginTop: 8, maxHeight: 180, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                          {groupUsers.length === 0 ? (
                            <div style={{ fontSize: 13, color: '#6b7280' }}>No users found.</div>
                          ) : groupUsers.map(u => (
                            <label key={u.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={selectedUserIds.has(u.uuid)}
                                onChange={() => toggleUser(u.uuid)}
                              />
                              <span style={{ fontSize: 14 }}>
                                {u.name || 'Unnamed'} <span style={{ color: '#6b7280' }}>({u.email})</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <small style={{ color: '#6b7280', fontSize: 12 }}>
                Tick the box to target the whole group, or use "Choose specific" to pick individuals.
              </small>
            </div>

            <div className="s-admin-marketing-form-group">
              <div
                className="s-admin-marketing-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="s-admin-marketing-upload-icon">
                  <Upload size={24} />
                </div>
                <p className="s-admin-marketing-upload-text">
                  {uploadedFile ? uploadedFile.name : 'Drop your CSV file here'}
                </p>
                <p className="s-admin-marketing-upload-subtext">
                  or click to browse from your computer
                </p>
                <button
                  type="button"
                  className="s-admin-marketing-upload-btn"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  Select File
                </button>
                <p className="s-admin-marketing-upload-hint">Supports .csv files up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <p className="s-admin-marketing-upload-hint" style={{ marginTop: 8 }}>
                Need the format?{' '}
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: '#f59e0b', textDecoration: 'underline', font: 'inherit'
                  }}
                >
                  Download CSV template
                </button>
              </p>

              {(csvEmails.length > 0 || csvInvalidEmails.length > 0) && (
                <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', background: '#f9fafb', fontSize: 13
                  }}>
                    <span style={{ fontWeight: 600 }}>
                      {uploadedFile ? uploadedFile.name : 'Imported recipients'}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ color: '#6b7280' }}>
                        {csvEmails.length} valid · {csvInvalidEmails.length} invalid
                      </span>
                      <button
                        type="button"
                        onClick={() => { setUploadedFile(null); setCsvEmails([]); setCsvInvalidEmails([]); }}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          color: '#ef4444', fontSize: 12, textDecoration: 'underline'
                        }}
                      >
                        Remove
                      </button>
                    </span>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto', padding: '6px 12px' }}>
                    {csvEmails.map((e, i) => (
                      <div key={`v-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                        <span>{e}</span>
                        <span style={{ marginLeft: 'auto', color: '#10b981', fontSize: 12 }}>Valid</span>
                      </div>
                    ))}
                    {csvInvalidEmails.map((e, i) => (
                      <div key={`i-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13, color: '#6b7280' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                        <span style={{ textDecoration: 'line-through' }}>{e}</span>
                        <span style={{ marginLeft: 'auto', color: '#ef4444', fontSize: 12 }}>Not in system</span>
                      </div>
                    ))}
                  </div>
                  {csvInvalidEmails.length > 0 && (
                    <div style={{ padding: '6px 12px', background: '#fef2f2', color: '#991b1b', fontSize: 12 }}>
                      Invalid recipients aren't registered users and will be skipped when sending.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="s-admin-marketing-form-row">
              <div className="s-admin-marketing-form-group s-admin-marketing-form-group-half">
                <label className="s-admin-marketing-form-label">Schedule</label>
                <select
                  className="s-admin-marketing-form-select"
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value)}
                >
                  <option value="now">Send Now</option>
                  <option value="schedule">Schedule</option>
                </select>
              </div>
              <div className="s-admin-marketing-form-group s-admin-marketing-form-group-half">
                <label className="s-admin-marketing-form-label">Date & Time *</label>
                <input
                  type="datetime-local"
                  className="s-admin-marketing-form-input"
                  value={scheduleDate}
                  min={minDateTime}
                  onChange={handleScheduleDateChange}
                  disabled={scheduleType === 'now'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="s-admin-marketing-preview-card">
            <div className="s-admin-marketing-preview-header">
              <Mail size={16} className="s-admin-marketing-preview-icon" />
              <span>Email Preview</span>
            </div>

            <div className="s-admin-marketing-preview-content">
              <div className="s-admin-marketing-preview-from">
                <span className="s-admin-marketing-preview-label">From:</span>
                <span>noreply@company.com</span>
              </div>
              <div className="s-admin-marketing-preview-to">
                <span className="s-admin-marketing-preview-label">To:</span>
                <span>Select audience</span>
              </div>

              <div className="s-admin-marketing-preview-message">
                <h3 className="s-admin-marketing-preview-title">
                  {notificationTitle || 'Your notification title'}
                </h3>
                <p className="s-admin-marketing-preview-text">
                  {message || 'Your message will appear here...'}
                </p>
              </div>
            </div>

            <div className="s-admin-marketing-ready-banner">
              <Info size={16} />
              <div className="s-admin-marketing-ready-content">
                <span className="s-admin-marketing-ready-title">Ready to Send</span>
                <ul className="s-admin-marketing-ready-list">
                  <li>• {selectedCount} recipient{selectedCount !== 1 ? 's' : ''}/segment{selectedCount !== 1 ? 's' : ''} selected</li>
                  <li>• Type: {notificationType}</li>
                  <li>• {scheduleType === 'now' ? 'Send immediately' : `Scheduled: ${scheduleDate}`}</li>
                </ul>
              </div>
            </div>

            {feedback && (
              <div className={`s-admin-marketing-feedback s-admin-marketing-feedback-${feedback.type}`}>
                {feedback.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                <span>{feedback.message}</span>
              </div>
            )}

            <div className="s-admin-marketing-actions">
              <button
                className="s-admin-marketing-draft-btn"
                onClick={handleSaveAsDraft}
                disabled={isSavingDraft || sending}
              >
                {isSavingDraft ? (
                  <>
                    <Loader2 size={18} className="s-admin-marketing-spinner" />
                    Saving Draft...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {editingUuid ? 'Update Draft' : 'Save as Draft'}
                  </>
                )}
              </button>
              <button
                className="s-admin-marketing-send-btn"
                onClick={handleSendNotification}
                disabled={sending || isSavingDraft}
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="s-admin-marketing-spinner" />
                    {scheduleType === 'now' ? 'Sending...' : 'Scheduling...'}
                  </>
                ) : (
                  <>
                    <Mail size={18} />
                    {scheduleType === 'now' ? 'Send Notification' : 'Schedule Notification'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeNotification;
